"use client";

import { Sparkles, Sun, X } from "lucide-react";
import Link from "next/link";
import { useEffect, useState } from "react";
import { apiUrl } from "@/lib/aicheck/api-base";
import { projectDetailPath } from "@/lib/aicheck/routes";
import { authService } from "@/services/auth";

interface DigestItem {
  notificationId: string;
  projectKey: string;
  title: string;
  gist: string | null;
  createdAt: string;
}

interface DigestResponse {
  count: number;
  projectCount: number;
  metaGist: string | null;
  items: DigestItem[];
}

const DISMISS_KEY = "aicheck-digest-dismissed";

/**
 * 変化ダイジェスト（②）バナー。案件一覧の上部で「最近の変化の俯瞰」を出す。
 * - 件数・案件数はコード確定。metaGist（テーマ1文）はローカル LLM 由来（数値なし・任意）。
 * - 変化が無い / 取得失敗なら何も描画しない（グレースフル劣化）。
 * - セッション中は×で閉じられる（閉じたら再表示しない）。
 */
export function DigestBanner() {
  const [data, setData] = useState<DigestResponse | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem(DISMISS_KEY) === "1") {
      setDismissed(true);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const token = await authService.getIdToken();
        if (!token || cancelled) return;
        const res = await fetch(apiUrl("/api/aicheck/notifications/digest"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok || cancelled) return;
        const json = (await res.json()) as DigestResponse;
        if (!cancelled) setData(json);
      } catch {
        // 付加機能。失敗は静かに無表示。
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (dismissed || !data || data.count === 0) return null;

  const dismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, "1");
    setDismissed(true);
  };

  return (
    <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Sun size={18} className="mt-0.5 shrink-0 text-amber-500" />
          <div>
            <p className="text-sm font-semibold text-amber-900">
              最近の変化: {data.projectCount}案件で新規の指摘
            </p>
            {data.metaGist && (
              <p className="mt-0.5 flex items-start gap-1 text-xs text-amber-800">
                <Sparkles
                  size={12}
                  className="mt-0.5 shrink-0 text-amber-500"
                />
                <span>{data.metaGist}</span>
              </p>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="shrink-0 rounded-md p-1 text-amber-400 hover:bg-amber-100 hover:text-amber-600"
          aria-label="閉じる"
        >
          <X size={16} />
        </button>
      </div>

      <ul className="mt-2 space-y-1 pl-6">
        {data.items.slice(0, 4).map((item) => (
          <li key={item.notificationId} className="text-xs text-amber-800">
            <Link
              href={projectDetailPath(item.projectKey)}
              className="hover:underline"
            >
              <span className="font-medium">{item.title}</span>
              {item.gist && (
                <span className="text-amber-700"> — {item.gist}</span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
